import { GenericColumnModel, CurrencyColumnModel, DateColumnModel, FilterSortPageType, FormattedReferenceColumnModel, NumberColumnModel, DateTimeColumnModel, TimeColumnModel } from "components/Table";
import { prepareFiltersModel } from "components/Table/RemoteTable/prepareFiltersModel";
import { exportedTranslationsState } from "components/Translations/TranslationsProvider";
import { isNotTranslated } from "components/Translations/translationUtils";
import { ExportModel } from "features/Export/ExportForm";
import { useCallback } from 'react';
import { ExportDataDecoraterPropertyModel, ExportDataModel, ExportDataPropertyModel } from "services/tenantManagementService";
import { Subtract } from "utility-types";
import { useGetDatePatternString, useGetDateTimePatternString, useGetTimePatternString } from 'utils/dateTimeUtils';
import { capitalizeFirstLetter } from "utils/stringUtil";

export type BackendColumnFields = {
	// filter
	dbFilterFieldPath?: string // DB field or path such as Status.Name for filtering
	dbFilterInnerFieldPath?: string // inner DB field path such as UserId (Follwers list -> UserId prop from Follower)
	// patch request
	patchRequestColumnId?: string // when sending patch request to update some cell, we need database column id
	// export
	dbExportFieldPath?: string | null // Db field or path such as Status.Name for export
	format?: string // for backend to format dates, FormattedReference, etc.
	displayNames?: string[] // for columns with decorator on server: userId -> username
	decoraterProperties?: ExportDataDecoraterPropertyModel[] // for columns with decorator on server: userId -> username
}

export type BackendColumnFieldsMap = {
	[columnId: string]: BackendColumnFields | undefined
}

type ExportColumnsType = Subtract<BackendColumnFields, { dbFilterFieldPath?: string }> & {
	visible: boolean
	title: string
}

// apply default values to backend export fields if they are not defined
const applyDefaults = (column: GenericColumnModel, specificBackendColumnFields: BackendColumnFields | undefined, datePattern: string, timePattern: string, dateTimePattern: string) => {
	const backendFields = { ...specificBackendColumnFields };

	// now lets add some default values where needed

	if (backendFields.dbExportFieldPath === undefined) {
		backendFields.dbExportFieldPath = capitalizeFirstLetter(column.id);
	}

	if (column instanceof DateColumnModel) {
		backendFields.format = backendFields.format || `{0:${datePattern}}`; // for backend
	}

	if (column instanceof TimeColumnModel) {
		// duration format as number by default
		backendFields.format = backendFields.format ||  column.isDuration ? `{0:0.00}` : `{0:${timePattern}}`; // for backend
	}

	if (column instanceof DateTimeColumnModel) {
		backendFields.format = backendFields.format || `{0:${dateTimePattern}}`; // for backend
	}

	if (column instanceof NumberColumnModel) {
		backendFields.format = backendFields.format || '{0:0.00}'; // for backend
	}

	if (column instanceof FormattedReferenceColumnModel) {
		backendFields.format = backendFields.format || `${column.entityPrefix}{0:D8}`
	}

	if (column instanceof CurrencyColumnModel) {
		// Svetlana je trazila da format ne sadrzi valutu
		// column.format = `{0:0.00} ${persistedCurrency.itemsMap[persistedTenant.value.currencyId]?.symbol}`;
		backendFields.format = backendFields.format || '{0:0.00}';
	}

	return backendFields;
}

const usePrepareExportColumns = () => {
	const datePattern = useGetDatePatternString();
	const timePattern = useGetTimePatternString();
	const dateTimePattern = useGetDateTimePatternString();

	return useCallback(
		(
			visibleColumns: GenericColumnModel[],
			hiddenColumns: GenericColumnModel[],
			specificBackendColumnFieldsMap: BackendColumnFieldsMap
		) => {
			const allTableColumns = [...visibleColumns, ...hiddenColumns];
			const exportColumns: ExportColumnsType[] = [];

			for (const tableColumn of allTableColumns) {
				const backendFields = applyDefaults(tableColumn, specificBackendColumnFieldsMap[tableColumn.id], datePattern, timePattern, dateTimePattern);

				const exportColumn: ExportColumnsType = {
					visible: visibleColumns.includes(tableColumn),
					title: tableColumn.title,
					...backendFields
				}

				exportColumns.push(exportColumn);
			}

			return exportColumns;
		},
		[datePattern, dateTimePattern, timePattern]
	)

}

const createPropertiesForExportModel = (exportColumns: ExportColumnsType[], exportModel: ExportModel) => {
	let columnsForExport = exportColumns.filter(col => col.dbExportFieldPath);

	if (exportModel.useOnlyVisible) {
		// because of undefined
		columnsForExport = columnsForExport.filter(col => !!col.visible)
	}

	const translations = exportedTranslationsState.translations;

	const properties = columnsForExport.map((column) => {
		const displayName = isNotTranslated(column.title) ? translations[column.title] : column.title;
		const displayNames = column.displayNames?.map((item) => isNotTranslated(item) ? translations[item] : item);
		const decoraterProperties = column.decoraterProperties?.map((item) => {
			item.name = isNotTranslated(item.name) ? translations[item.name] : item.name
			return item;
		});

		return new ExportDataPropertyModel({
			name: column.dbExportFieldPath!,
			displayName,
			format: column.format,
			displayNames,
			decoraterProperties
		})
	})

	return properties;
}

export const usePrepareExportModel = () => {
	const prepareExportColumns = usePrepareExportColumns();

	return useCallback(
		(
			visibleColumns: GenericColumnModel[],
			hiddenColumns: GenericColumnModel[],
			specificBackendColumnFieldsMap: BackendColumnFieldsMap,
			exportModel: ExportModel,
			filterSortPage: FilterSortPageType
		) => {
			const exportColumns = prepareExportColumns(visibleColumns, hiddenColumns, specificBackendColumnFieldsMap);
			const properties = createPropertiesForExportModel(exportColumns, exportModel);

			const exportDataModel = new ExportDataModel({
				fileType: exportModel.exportType,
				// TODO: How to know C# time zone time?
				timeZone: 'Central Europe Standard Time',
				//timeZoneOffsetInMunutes: -new Date().getTimezoneOffset(),
				properties,
				offset: 0,
				limit: 0
			});

			const filtersModel = prepareFiltersModel(filterSortPage, specificBackendColumnFieldsMap);

			if (exportModel.applyFilters) {
				exportDataModel.filters = filtersModel.filters;
			}

			if (exportModel.applySort) {
				exportDataModel.sorts = filtersModel.sorts
			}

			if (exportModel.exportOnlyCurrentPage) {
				exportDataModel.offset = filtersModel.offset;
				exportDataModel.limit = filtersModel.limit;
			}

			exportDataModel.includes = filtersModel.includes;

			return exportDataModel;
		},
		[prepareExportColumns]
	)
}

export const useCreatePropertiesForExportModelForProjectReport = () => {
	const prepareExportColumns = usePrepareExportColumns();

	return useCallback(
		(
			visibleColumns: GenericColumnModel[],
			hiddenColumns: GenericColumnModel[],
			specificBackendColumnFieldsMap: BackendColumnFieldsMap,
			exportModel: ExportModel
		) => {
			const exportColumns = prepareExportColumns(visibleColumns, hiddenColumns, specificBackendColumnFieldsMap);
			const properties = createPropertiesForExportModel(exportColumns, exportModel);

			return properties;
		},
		[prepareExportColumns]
	)
}
